<script setup>
import { computed, watch } from 'vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

const route = useRoute();
const { t } = useI18n();
const store = useStore();

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update-attribute']);


const handleUpdate = async (value) => {
  const updatedAttributes = { key: props.attribute.attributeKey, value };

  if (route.params.inboxId) {
    try {
      await store.dispatch('inboxes/setSettingsWhatsappCortexwoot', {
        inboxId: route.params.inboxId,
        customAttributes: updatedAttributes,
      });

      useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
    } catch (error) {
      useAlert(
        error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
      );
    }
  }
  emit('update-attribute', { key: props.attribute.attributeKey, value });
};

const handleRejectionMessageUpdate = async (event) => {
  const rejectionMessage = event.target.value;
  const updatedAttributes = {
    [props.attribute.attributeKey]: true,
    msgCall: rejectionMessage
  };

  if (route.params.inboxId) {
    try {
      await store.dispatch('inboxes/setSettingsWhatsappCortexwoot', {
        inboxId: route.params.inboxId,
        customAttributes: updatedAttributes,
      });
      useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
    } catch (error) {
      useAlert(
        error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
      );
    }
  }
  emit('update-attribute', updatedAttributes);
};

const componentMap = {
  checkbox: CheckboxAttribute,
  number: OtherAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return componentMap[props.attribute.attributeDisplayType] || componentMap.default;
});

// Computed para ícones específicos baseados na funcionalidade
const getSpecificIcon = computed(() => {
  const displayName = props.attribute.attributeDisplayName?.toLowerCase() || '';
  const attributeKey = props.attribute.attributeKey?.toLowerCase() || '';
  
  // Mapeamento específico baseado na funcionalidade
  const iconMap = {
    // Chamadas
    'rejectcall': '📵',
    'chamadas': '📞',
    'rejeitar': '📵',
    
    // Grupos
    'ignoregroups': '👥',
    'grupos': '👥',
    'ignorar': '🚫',
    
    // Status online
    'alwaysonline': '🟢',
    'online': '🟢',
    'mantenha': '🟢',
    
    // Mensagens lidas
    'markread': '✅',
    'lidas': '✅',
    'marcar': '✅',
    
    // Sincronização/Histórico
    'syncchat': '🔄',
    'histórico': '🔄',
    'sincronize': '🔄',
    'importar': '📥',
    
    // Status
    'markstatus': '👁️',
    'status': '👁️',
    
    // Assinatura
    'signature': '✍️',
    'assinar': '✍️',
    'usuário': '👤',
    
    // Conversa
    'reopenconversation': '💬',
    'reabrir': '💬',
    'conversa': '💬',
    
    // Pendente
    'pendingconversations': '⏳',
    'pendentes': '⏳',
    'começam': '⏳',
    
    // Contatos
    'importcontacts': '📇',
    'contatos': '📇',
    'agenda': '📇',
    
    // Merge/Mesclar
    'mergecontacts': '🔀',
    'mesclar': '🔀',
    
    // Limite/Dias
    'limitdays': '📅',
    'limite': '📅',
    'dias': '📅',
    
    // QR Code
    'qrcode': '📱',
    'qr': '📱',
  };
  
  // Procurar por palavras-chave no nome do atributo
  for (const [key, icon] of Object.entries(iconMap)) {
    if (displayName.includes(key) || attributeKey.includes(key)) {
      return icon;
    }
  }
  
  // Fallback baseado na categoria
  if (props.attribute.category === 'whatsapp') {
    return '📱';
  } else if (props.attribute.category === 'chatwoot') {
    return '💬';
  }
  
  return '⚙️'; // Ícone padrão
});
</script>

<template>
  <div class="max-w-7xl w-full flex flex-col">
    <div
      class="grid grid-cols-[1fr,auto] group/attribute items-center w-full gap-4 p-1 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
      :class="isEditingView ? 'min-h-10' : 'min-h-11'"
    >
      <div class="flex items-center gap-3 min-w-0">
        <!-- Ícone específico da funcionalidade -->
        <span class="text-lg flex-shrink-0" :title="attribute.category">
          {{ getSpecificIcon }}
        </span>
        
        <!-- Título e categoria -->
        <div class="min-w-0 flex-1">
          <span class="text-sm font-medium text-slate-900 dark:text-slate-100 block">
            {{ attribute.attributeDisplayName }}
          </span>

        </div>
      </div>
      
      <div class="flex-shrink-0">
        <component
          :is="CurrentAttributeComponent"
          :attribute="attribute"
          :is-editing-view="isEditingView"
          @update="handleUpdate"
        />
      </div>
    </div>
    
    <!-- Input condicional para "Rejeitar todas as chamadas recebidas" -->
    <div v-if="attribute.attributeKey === 'rejectCall' && attribute.value" class="mt-2 ml-4">
      <textarea
        v-model="attribute.msgCall"
        placeholder="Informe o motivo da rejeição que será enviado automaticamente..."
        @blur="handleRejectionMessageUpdate"
        rows="3"
        class="w-full max-w-7xl p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md 
               bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
               placeholder-slate-400 dark:placeholder-slate-500
               focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
               transition-colors resize-none"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        Esta mensagem será enviada quando uma chamada for rejeitada
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Hover suave para os itens */
.group\/attribute:hover {
  @apply bg-slate-50 dark:bg-woot-900;
}

/* Focus styles para textarea */
textarea:focus {
  @apply outline-none;
}

/* Estados responsivos */
@media (max-width: 640px) {
  .text-sm {
    @apply text-xs;
  }
  
  .text-lg {
    @apply text-base;
  }
}
</style>